import { useState } from "react";

const contactDetails = [
  { label: "Address", value: "Dolenjska cesta 274", icon: "ri-map-pin-line" },
  { label: "City", value: "Skofljica", icon: "ri-building-line" },
  { label: "Zip", value: "1291", icon: "ri-mail-open-line" },
  { label: "Country", value: "Slovenia", icon: "ri-global-line" },
  { label: "Email", value: "help@liwasdoo.com", icon: "ri-mail-line" },
  { label: "Phone", value: "+1 6626855661", icon: "ri-phone-line" },
];

export default function ContactSection() {
  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > 500) return;

    setFormState("submitting");
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      if (typeof value === "string") data.append(key, value);
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d77r8lqdqn9mib2tqcmg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setFormState("success");
        form.reset();
        setCharCount(0);
      } else {
        setFormState("error");
      }
    } catch {
      setFormState("error");
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-gray-900">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 mb-8">
            <span className="text-xs font-semibold tracking-widest uppercase text-white/60">Contact Us</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight">
            Get in Touch<br />
            <span className="text-white/40">We&apos;d Love to Hear</span><br />
            <span className="text-white/40">From You.</span>
          </h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Contact info */}
          <div className="lg:w-2/5">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-8 md:p-10">
              <p className="text-white/60 text-sm leading-relaxed mb-8">
                Have a question about our products or want to learn more about LIWAS d.o.o.? Reach out to us using any of the contact details below.
              </p>

              <ul className="flex flex-col gap-5">
                {contactDetails.map((item) => (
                  <li key={item.label} className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-white/70 text-base`} />
                    </div>
                    <div>
                      <span className="block text-xs font-semibold tracking-widest uppercase text-white/40 mb-0.5">{item.label}</span>
                      <span className="text-white text-sm font-medium">{item.value}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact form */}
          <div className="lg:w-3/5">
            <form
              data-readdy-form
              id="liwas-contact-form"
              onSubmit={handleSubmit}
              className="rounded-2xl bg-white/5 border border-white/10 p-8 md:p-10 flex flex-col gap-5"
            >
              <div className="flex flex-col sm:flex-row gap-5">
                <div className="flex-1 flex flex-col gap-2">
                  <label className="text-xs font-semibold tracking-widest uppercase text-white/50">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="John Doe"
                    className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                  />
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <label className="text-xs font-semibold tracking-widest uppercase text-white/50">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold tracking-widest uppercase text-white/50">Subject</label>
                <input
                  type="text"
                  name="subject"
                  required
                  placeholder="How can we help?"
                  className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                />
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold tracking-widest uppercase text-white/50">Message</label>
                  <span className={`text-xs ${charCount > 500 ? "text-red-400" : "text-white/30"}`}>{charCount}/500</span>
                </div>
                <textarea
                  name="message"
                  required
                  rows={5}
                  placeholder="Tell us more about your inquiry..."
                  maxLength={500}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
                />
              </div>

              {formState === "success" && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-emerald-500/20 border border-emerald-500/30">
                  <i className="ri-checkbox-circle-line text-emerald-400 text-lg" />
                  <span className="text-emerald-300 text-sm font-medium">Message sent successfully! We&apos;ll get back to you soon.</span>
                </div>
              )}

              {formState === "error" && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-500/20 border border-red-500/30">
                  <i className="ri-error-warning-line text-red-400 text-lg" />
                  <span className="text-red-300 text-sm font-medium">Something went wrong. Please try again.</span>
                </div>
              )}

              <button
                type="submit"
                disabled={formState === "submitting" || charCount > 500}
                className="w-full py-3.5 rounded-lg bg-white text-gray-900 text-sm font-semibold tracking-wide cursor-pointer whitespace-nowrap hover:bg-gray-100 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formState === "submitting" ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>

        {/* Footer bottom bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-white/30 text-xs">
            &copy; {new Date().getFullYear()} LIWAS d.o.o. All rights reserved.
          </span>
          <span className="text-white/30 text-xs">
            Dolenjska cesta 274, 1291 Skofljica, Slovenia
          </span>
        </div>
      </div>
    </section>
  );
}
